/**
 * Schema.org AggregateRating JSON-LD. Primary use is as a HELPER —
 * {@link buildAggregateRatingSchema} returns the bare object that
 * embeds into Course / Product / Book schemas under their
 * {@code aggregateRating} field. Standalone use is uncommon (Google
 * usually wants this attached to a parent entity).
 */
import { JsonLdScript } from './_shared';

export interface AggregateRatingSchemaInput {
  ratingValue: number;
  /** Either {@code ratingCount} or {@code reviewCount} is required. */
  ratingCount?: number;
  reviewCount?: number;
  /** Scale ceiling. Default 5. */
  bestRating?: number;
  /** Scale floor. Default 1. */
  worstRating?: number;
}

export interface AggregateRatingSchemaJsonLdProps extends AggregateRatingSchemaInput {
  id?: string;
}

export function AggregateRatingSchemaJsonLd({ id, ...rating }: AggregateRatingSchemaJsonLdProps) {
  const schema = buildAggregateRatingSchema(rating);
  if (!schema) return null;
  return <JsonLdScript schema={schema} id={id ?? 'schema-aggregaterating'} />;
}

export function buildAggregateRatingSchema(rating: AggregateRatingSchemaInput) {
  const hasCount = (rating.ratingCount ?? 0) > 0 || (rating.reviewCount ?? 0) > 0;
  if (!hasCount) return null;
  return {
    '@type': 'AggregateRating',
    ratingValue: rating.ratingValue,
    ratingCount: rating.ratingCount,
    reviewCount: rating.reviewCount,
    bestRating: rating.bestRating ?? 5,
    worstRating: rating.worstRating ?? 1,
  };
}
